import { FieldErrors } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useConsumerCart, useModal } from 'shop/hooks'
import { CHECKOUT_ORIGIN } from 'shop/types'
import {
  CartErrorMessage,
  CartProductError,
  DiscountError,
  DiscountWarning,
  OrderItemWithVatPrices,
  InvalidTimeslotPrepTimeWarning,
  InvalidTimeslotWarning,
  OrderItem
} from 'shop/types/cart'
import { returnToShop } from 'shop/utils/common'
import DiscountErrorModal from '../ErrorModals/DiscountErrorModal'
import CartProductErrorModal from '../Modal/CartProductErrorModal/CartProductErrorModal'
import ErrorModal from '../Modal/ErrorModal'
import InvalidFulfillmentTypeModal from '../Modal/InvalidFulfillmentTypeModal'
import { trackMovActioned } from './tracking/helpers'
import { FormFields } from './types'
import { getDiscountErrorWarningValues } from './utils'

// Determines whether error modals trigger checkout page form submission ("PAY") or not ('PAGE")
type ErrorModalType = 'PAGE' | 'PAY' | null

type Props = {
  showProductErrorModal: ErrorModalType
  showDiscountErrorModal: ErrorModalType
  productError: CartProductError | null
  invalidItems: (OrderItem | OrderItemWithVatPrices)[]
  invalidFulfillmentTypeError: CartErrorMessage.INVALID_FULFILLMENT_TYPE | null
  handleProceedWithPriceChange: (submitForm: boolean) => void
  handleRemoveOrderItemsFromCart: (submitForm: boolean) => void
  handleRemoveDiscountViaModal: (submitForm: boolean) => void
  handleUpdateOrderItems: (submitForm: boolean) => void
  activeTimeSlotValidation:
    | InvalidTimeslotWarning
    | InvalidTimeslotPrepTimeWarning
    | null
  hasSubtotalDiscount: boolean
  isFulfillmentToggleTriggered: boolean
  discountErrorWarnings: (DiscountWarning | DiscountError)[]
  formErrors: FieldErrors<FormFields>
}

const CheckoutErrorModals = ({
  showProductErrorModal,
  showDiscountErrorModal,
  productError,
  invalidItems,
  invalidFulfillmentTypeError,
  handleProceedWithPriceChange,
  handleRemoveOrderItemsFromCart,
  handleRemoveDiscountViaModal,
  handleUpdateOrderItems,
  activeTimeSlotValidation,
  isFulfillmentToggleTriggered,
  discountErrorWarnings,
  hasSubtotalDiscount,
  formErrors
}: Props) => {
  const { cart, cartLoading, validateCart } = useConsumerCart()
  const { isModalOpen, loginModal, timeSlotModal } = useModal()
  const navigate = useNavigate()

  if (!cart) return <></>

  const fulfillmentType = cart.fulfillment.type
  const storeSlug = cart.store.slug

  const handleProductErrorModalOnContinue = () => {
    if (!productError) return

    if (productError.message === CartErrorMessage.PRODUCT_PRICES_CHANGED) {
      return handleProceedWithPriceChange(showProductErrorModal === 'PAY')
    } else if (
      productError.message === CartErrorMessage.PRODUCT_LIMIT_REACHED
    ) {
      return handleUpdateOrderItems(showProductErrorModal === 'PAY')
    } else {
      return handleRemoveOrderItemsFromCart(showProductErrorModal === 'PAY')
    }
  }

  return (
    <>
      {showProductErrorModal && productError && (
        <CartProductErrorModal
          loading={cartLoading}
          productError={productError}
          invalidItems={invalidItems}
          storeName={cart.store.name}
          onContinue={handleProductErrorModalOnContinue}
        />
      )}
      {invalidFulfillmentTypeError && fulfillmentType && (
        <InvalidFulfillmentTypeModal
          currentFulfillmentType={fulfillmentType}
          isFulfillmentToggleTriggered={isFulfillmentToggleTriggered}
          onContinue={() => validateCart(false)}
        />
      )}
      {isModalOpen('login') && loginModal({})}
      {isModalOpen('timeslot') &&
        activeTimeSlotValidation &&
        timeSlotModal(activeTimeSlotValidation)}
      {!!discountErrorWarnings.length && showDiscountErrorModal && (
        <DiscountErrorModal
          errorCode={discountErrorWarnings[0].message}
          values={getDiscountErrorWarningValues(discountErrorWarnings[0])}
          origin={CHECKOUT_ORIGIN}
          checkoutRemoveDiscount={() =>
            handleRemoveDiscountViaModal(showDiscountErrorModal === 'PAY')
          }
          currentFulfillmentType={fulfillmentType}
          storeSlug={storeSlug}
        />
      )}
      {!!formErrors?.['checkout_cart'] && (
        <ErrorModal
          errorCode='MOV_ERROR'
          errorMessages={[formErrors['checkout_cart'].message]}
          errorTitle='Minimum value not met'
          primaryButton={{
            onClick: () => {
              trackMovActioned()
              returnToShop(storeSlug, navigate)
            },
            message: 'Add more items'
          }}
          secondaryButton={
            hasSubtotalDiscount
              ? {
                  onClick: () => handleRemoveDiscountViaModal(false),
                  message: 'Remove discount code'
                }
              : undefined
          }
        />
      )}
    </>
  )
}

export default CheckoutErrorModals
